"use client";

import { Suspense, useState } from "react";
import * as z from "zod";

import Icon from "@/components/atoms/Icon";
import { Sausage } from "@/components/atoms/Sausage";
import {
  LocationValueSelectProps,
  NewLocationInput,
} from "@/components/forms/controls/NewLocationInput";
import Select from "@/components/forms/controls/Select";
import Toggle from "@/components/forms/controls/Toggle";
import Form from "@/components/forms/Form";
import { Filters } from "@/components/organisms/Filters";
import { FiltersSidebar } from "@/components/organisms/FiltersSidebar";
import { GetOfficesQuery } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import { niches } from "@/helpers/niche";
import { useGetParams, useRefreshSearchParams } from "@/helpers/search";
import {
  AllowedJobSearchParams,
  AllowedLanguages,
  AllowedOfficeSearchParams,
} from "@/types/generic";
import { OfficeMap } from "../molecules/OfficeMap";

const schema = z.object({
  [AllowedJobSearchParams.City]: z.string().optional(),
  [AllowedJobSearchParams.MaxDistance]: z.string().optional(),
  [AllowedJobSearchParams.Niche]: z.array(z.string()).optional(),
});
type Schema = z.infer<typeof schema>;

interface SearchOfficesFilterFormProps {
  allOffices: NonNullable<GetOfficesQuery["offices"]>;
  children: React.ReactNode;
  i18n: {
    active_filters: string;
    clear_filters: string;
    close_sidebar: string;
    distance: string;
    filter_niche: string;
    no_results: string;
    offices_found: string;
    search_location_placeholder: string;
    show: string;
    title: string;
    use_current_location: string;
    view_list: string;
    view_map: string;
  };
  locale: AllowedLanguages;
  offices: NonNullable<GetOfficesQuery["offices"]>;
}

const DEFAULT_DISTANCE = "15";

const distanceOptions = [
  { value: "5", label: "5 km" },
  { value: "10", label: "10 km" },
  { value: "15", label: "15 km" },
  { value: "20", label: "20 km" },
  { value: "50", label: "50 km" },
];

const filters = niches;

export const SearchOfficesFilterForm = ({
  allOffices,
  i18n,
  locale,
  offices,
  children,
}: SearchOfficesFilterFormProps) => {
  const params = useGetParams();
  const [viewMode, setViewMode] = useState<"map" | "list">("map");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const defaultValues: Schema = {
    [AllowedOfficeSearchParams.City]:
      params.get(AllowedOfficeSearchParams.City) ?? "",
    [AllowedOfficeSearchParams.MaxDistance]:
      params.get(AllowedOfficeSearchParams.MaxDistance) ?? DEFAULT_DISTANCE,
    [AllowedOfficeSearchParams.Niche]:
      params.get(AllowedOfficeSearchParams.Niche)?.split("|") ?? [],
  };

  const { updateFilter, updateQuery, form, selectedFilters } =
    useRefreshSearchParams(
      schema,
      [AllowedOfficeSearchParams.Niche],
      defaultValues,
    );

  const handleSubmit = (query: Schema) => {
    updateQuery("", query?.city ?? "", query?.max_distance ?? "", 1);
  };

  const updateLocation = ({
    latitude,
    longitude,
    location,
  }: LocationValueSelectProps) => {
    updateFilter(AllowedOfficeSearchParams.Lat, latitude.toString());
    updateFilter(AllowedOfficeSearchParams.Lng, longitude.toString());
    updateFilter(AllowedOfficeSearchParams.City, location);
  };

  return (
    <>
      <div className="relative z-1 pb-4 pt-8 lg:pb-0 lg:pt-12 xl:pt-16">
        <div className="container">
          <Form form={form} onSubmit={handleSubmit}>
            <div className="mb-4 flex flex-col flex-wrap gap-6 md:flex-row md:items-center md:justify-between lg:mb-12 lg:gap-[220px]">
              <h1 className="title-line !mb-0">{i18n.title}</h1>

              <div className="flex items-center rounded-lg border lg:flex-grow">
                <NewLocationInput
                  onSelectValue={updateLocation}
                  className="w-full"
                  inputClassName="form-input-lg !rounded-r-none rounded-l-lg ring-0 focus:ring-0"
                  inputName="city"
                  i18n={{
                    placeholder: i18n.search_location_placeholder,
                    use_current_location: i18n.use_current_location,
                  }}
                  locale={locale}
                />
                <Select
                  label={i18n.distance}
                  name="max_distance"
                  className="divider-l flex"
                  inputClassName="!rounded-l-none rounded-r-lg pl-4 pr-6 lg:pr-10 ring-0 focus:ring-0 w-[88px] lg:!w-24 text-sm"
                  defaultValue={DEFAULT_DISTANCE}
                  options={distanceOptions}
                  hiddenLabel={true}
                  onChange={(e) => {
                    if (e?.target) {
                      updateFilter(
                        AllowedOfficeSearchParams.MaxDistance,
                        e.target.value,
                      );
                    }
                  }}
                />
              </div>
            </div>

            <Suspense
              fallback={<div className={"hidden h-[450px] border lg:block"} />}
            >
              <OfficeMap
                className="hidden lg:block"
                allOffices={allOffices}
                filteredOffices={offices}
                locale={locale}
              />
            </Suspense>

            <Sausage
              className="bg-white lg:hidden"
              onClick={() => {
                setFiltersOpen(true);
              }}
            >
              {i18n.filter_niche}
            </Sausage>

            <div className="hidden flex-wrap items-center gap-2 lg:mb-10 lg:flex">
              {filters.map((filter) => (
                <Toggle
                  label={filter?.label[locale]}
                  name={AllowedOfficeSearchParams.Niche}
                  onCheckedChange={updateFilter}
                  selectedFilters={selectedFilters}
                  value={filter?.value ?? ""}
                  key={filter.value}
                  badgeIcon="dot"
                  badgeIconClassName={cn("w-2 h-2", filter.color)}
                  checkedClassName="!border-orange lg:!border-yellow"
                ></Toggle>
              ))}
            </div>
            <FiltersSidebar
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersOpen}
              i18n={{
                clear_filters: i18n.clear_filters,
                close_sidebar: i18n.close_sidebar,
                show: i18n.show,
                entity_title: i18n.offices_found,
              }}
              resultCount={offices?.length}
            >
              <Filters
                updateFilter={updateFilter}
                selectedFilters={selectedFilters}
                filters={[
                  {
                    name: "SPECIALITY",
                    values: filters,
                  },
                ]}
                filterOrder={[AllowedOfficeSearchParams.Niche]}
                idPrefix={"mobile_"}
                i18n={{
                  active_filters: i18n.active_filters,
                  clear_filters: i18n.clear_filters,
                }}
                locale={locale}
              />
            </FiltersSidebar>
          </Form>
        </div>
      </div>

      <div className="container pb-10 pt-4 lg:pb-24 lg:pt-12">
        <div className="grid grid-cols-12">
          <div className="col-span-12 xl:col-span-10 xl:col-start-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="h5 text-black-300">
                {offices?.length} {i18n.offices_found}
              </h2>

              <div className="flex rounded-[40px] border bg-white p-1 lg:hidden">
                <button
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full",
                    viewMode === "map" ? "bg-black text-white" : "",
                  )}
                  onClick={() => setViewMode("map")}
                  title={i18n.view_map}
                  type="button"
                >
                  <Icon name="map" className="h-4 w-4" />
                </button>
                <button
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full",
                    viewMode === "list" ? "bg-black text-white" : "",
                  )}
                  onClick={() => setViewMode("list")}
                  title={i18n.view_list}
                  type="button"
                >
                  <Icon name="list" className="h-4 w-4" />
                </button>
              </div>
            </div>

            <Suspense
              fallback={<div className={"h-[450px] border lg:hidden"} />}
            >
              <OfficeMap
                className={cn("lg:hidden")}
                hidden={viewMode !== "map"}
                allOffices={allOffices}
                filteredOffices={offices}
                locale={locale}
              />
            </Suspense>

            <div className={viewMode !== "list" ? "hidden lg:block" : ""}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
